import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { isNil, uniqueId } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { LoadingDIcon, XCircleIcon } from '../Icons';
import { renderWithTooltip } from '../Tooltip';
import { Text } from '../Typography/Text';
import { UnderlineHint } from '../Typography/UnderlineHint';
import { renderRequiredLabel } from './Label';
import { StyledInputContainer, StyledInput, StyledIconWrapper, StyledLabel, InputWrap, StyledHelpText, StyledInputRoot, StyledAddOnWrapper, StyledLabelWithHint } from './styled';
import { tokens } from './tokens';
import { InputVariants, Placement, InputSize } from './types';
import { TriggerKind, useInput } from './useInput';
export const Input = /*#__PURE__*/ forwardRef(({ variant = InputVariants.primary, type = 'text', value, iconPlacement = Placement.right, addOnPlacement = Placement.left, addOnSeparator = true, icon, addOn, title, maxLength, required = false, label, inputSize = InputSize.m, placeholder, disabled, min, max, hint, hintWithUnderline, tooltip, delay = 0, preFormat, onChange, onPressEnter, onClear, onKeyDown, onBlur, helpText, allowEscapeSequence, style, className, allowClear, onClick, id, loading, noBorder, noBoxShadow, ...restProps }, ref)=>{
    const inputId = useMemo(()=>id || uniqueId('input'), [
        id
    ]);
    const helpTextId = useMemo(()=>uniqueId('input-help-text'), []);
    const { showClear, setFocus, _value, setRef, handleChange, handleBlur, handleKeyDown, handleClear, valueLength } = useInput({
        value,
        onChange,
        delay,
        allowEscapeSequence,
        onBlur,
        onPressEnter,
        onKeyDown,
        preFormat,
        ref,
        allowClear,
        onClear
    });
    const showIcon = icon && !(showClear && iconPlacement === Placement.right) && !(loading && iconPlacement === Placement.left);
    // validation for props, this will be trimmed on production build
    if (!isNodeEvnProduction()) {
        if (inputSize !== InputSize.l && title) {
            throw new Error('title prop is only available for large size input');
        }
    }
    let inputUI = /*#__PURE__*/ _jsxs(InputWrap, {
        align: "start",
        alignY: "start",
        direction: "vertical",
        addOnPlacement: addOn ? addOnPlacement : undefined,
        iconPlacement: icon ? iconPlacement : undefined,
        "data-placeholder": placeholder,
        "data-value": _value,
        children: [
            title && /*#__PURE__*/ _jsx(Text, {
                level: "xxs",
                color: tokens.Input.Title.color,
                children: title
            }),
            /*#__PURE__*/ _jsx(StyledInput, {
                ...restProps,
                ref: setRef,
                id: inputId,
                type: type,
                maxLength: maxLength,
                min: min,
                max: max,
                addOn: addOn,
                value: _value,
                placeholder: placeholder,
                onChange: (e)=>handleChange(e, TriggerKind.User),
                onBlur: handleBlur,
                disabled: disabled,
                onKeyDown: handleKeyDown,
                "aria-invalid": variant === InputVariants.error ? 'true' : undefined,
                "aria-describedby": helpText ? helpTextId : restProps['aria-describedby']
            })
        ]
    });
    if (!isNil(tooltip)) {
        inputUI = /*#__PURE__*/ _jsx(_Fragment, {
            children: renderWithTooltip(inputUI, tooltip)
        });
    }
    if (!label && hint) {
        inputUI = /*#__PURE__*/ _jsx(StyledLabelWithHint, {
            hint: hint,
            children: inputUI
        });
    }
    const labelUIWrapper = useMemo(()=>{
        const labelUI = label ? /*#__PURE__*/ _jsx(StyledLabel, {
            htmlFor: inputId,
            children: label
        }) : null;
        if (label && hintWithUnderline) {
            return /*#__PURE__*/ _jsx(UnderlineHint, {
                hint: hintWithUnderline,
                required: required,
                children: labelUI
            });
        }
        if (label && hint) {
            return /*#__PURE__*/ _jsx(StyledLabelWithHint, {
                level: "xs",
                hint: hint,
                children: labelUI
            });
        }
        return renderRequiredLabel({
            label: labelUI,
            required
        });
    }, [
        hint,
        hintWithUnderline,
        inputId,
        label,
        required
    ]);
    return /*#__PURE__*/ _jsxs(StyledInputRoot, {
        variant: variant,
        size: inputSize,
        direction: "vertical",
        gap: tokens.Root.gap,
        style: style,
        className: className,
        onClick: (e)=>{
            onClick?.(e);
            setFocus();
        },
        children: [
            labelUIWrapper,
            /*#__PURE__*/ _jsxs(StyledInputContainer, {
                type: type,
                variant: variant,
                inputSize: inputSize,
                disabled: disabled,
                noBorder: noBorder,
                noBoxShadow: noBoxShadow,
                children: [
                    inputUI,
                    showClear && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: Placement.right,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: /*#__PURE__*/ _jsx(XCircleIcon, {
                            type: "solid",
                            onClick: handleClear
                        })
                    }),
                    loading && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: Placement.left,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: /*#__PURE__*/ _jsx(LoadingDIcon, {
                            type: "extended",
                            spin: true
                        })
                    }),
                    showIcon && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: iconPlacement,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: icon
                    }),
                    addOn && /*#__PURE__*/ _jsx(StyledAddOnWrapper, {
                        size: inputSize,
                        $variant: variant,
                        iconPlacement: icon ? iconPlacement : undefined,
                        addOnPlacement: addOnPlacement,
                        addOnSeparator: !!addOnSeparator,
                        children: addOn
                    })
                ]
            }),
            helpText && /*#__PURE__*/ _jsxs(StyledHelpText, {
                id: helpTextId,
                variant: variant,
                direction: "horizontal",
                align: "space-between",
                role: variant === InputVariants.error ? 'alert' : undefined,
                children: [
                    helpText,
                    type !== 'number' && maxLength && maxLength > 0 ? /*#__PURE__*/ _jsx("span", {
                        children: valueLength + '/' + maxLength
                    }) : null
                ]
            })
        ]
    });
});
